import { useFeatureIsOn } from '@growthbook/growthbook-react';
import React, { useEffect, useState } from 'react';
import { DataCard } from '@/reports/shared/DataCard/DataCard';
import { EmptyVehicleSectionDisclaimer } from '@/reports/shared/ReportSection/EmptyVehicleSectionDisclaimer/EmptyVehicleSectionDisclaimer';
import { AutomobileSalvages } from '@/reports/types/vehicles';
import { PremiumDataCta } from '@/reports/vehicle/PremiumDataCta/PremiumDataCta';
import { getHowItWorksContent } from '@/reports/vehicle/utils';
import { Icons, Stack, Text } from '@/theme/index';
import { pluralizeString } from '@/utils/pluralizeString';
import { VehicleSalvageCard } from './VehicleSalvageCard';
import { PremiumSectionVariation } from '../PremiumSection/PremiumSectionVariation/PremiumSectionVariation';

interface VehicleSalvagesProps {
  vehicleSalvagesList: AutomobileSalvages;
  nmvtisSuccess: boolean | undefined;
  reportUpgraded: boolean;
  handleFreeUpgrade: () => void;
}

export function VehicleSalvages({
  vehicleSalvagesList,
  nmvtisSuccess,
  reportUpgraded,
  handleFreeUpgrade,
}: VehicleSalvagesProps) {
  const [title, setTitle] = useState<string | JSX.Element>('');

  const isInUnlockTitlesVariation = useFeatureIsOn('bump-3416');

  const howItWorksCTAContent = getHowItWorksContent('Salvage Records');

  useEffect(() => {
    if (vehicleSalvagesList?.length === 0 && !nmvtisSuccess) {
      setTitle(
        <Stack direction="row">
          <Icons.Lock />
          <Text variant="h2">Salvage Records</Text>
        </Stack>
      );
    } else {
      setTitle(
        `${vehicleSalvagesList?.length || 0} Salvage ${pluralizeString(
          'Record',
          vehicleSalvagesList?.length
        )} Reported`
      );
    }
  }, [vehicleSalvagesList?.length, nmvtisSuccess]);

  return (
    <DataCard testId="vehicle-salvages-report-section">
      <Text data-cy="report-section-title-text" variant="h2">
        {title}
      </Text>
      {vehicleSalvagesList &&
        vehicleSalvagesList.map((salvage, index) => (
          <VehicleSalvageCard salvage={salvage} key={index} />
        ))}
      {vehicleSalvagesList?.length > 0 && (
        <Stack direction="column">
          <Text
            fontSize="0.75rem"
            fontWeight="bold"
            component="span"
            sx={{ mb: 0.5 }}
          >
            EXPLANATORY NOTE:
          </Text>
          <Text fontSize="0.75rem" component="span">
            If this VIN has a record in the{' '}
            <strong>Junk/Salvage or Insurance information</strong> then the
            business that submitted the VIN to NMVTIS deemed the vehicle to be
            either a{' '}
            <em>junk, salvage, or in the case of an insurer, a total loss</em>.
            The information in the <strong>DISPOSITION</strong> field in the
            Junk/Salvage section denotes what has happened to the VIN (i.e.,
            vehicle) since it came into the possession of the business.
          </Text>
        </Stack>
      )}
      {!reportUpgraded || !nmvtisSuccess ? (
        isInUnlockTitlesVariation ? (
          <PremiumSectionVariation
            sectionId="premium-salvage-records"
            sectionName="Salvage Records"
            nmvtisSuccess={nmvtisSuccess}
            reportUpgraded={reportUpgraded}
            inSubsection
            handleFreeUpgrade={handleFreeUpgrade}
            showTopBar
          />
        ) : (
          <PremiumDataCta
            isSection
            sectionName="Salvage"
            nmvtisSuccess={nmvtisSuccess}
            reportUpgraded={reportUpgraded}
            handleFreeUpgrade={handleFreeUpgrade}
          />
        )
      ) : (
        vehicleSalvagesList?.length === 0 && (
          <>
            <EmptyVehicleSectionDisclaimer title="salvage" />
            {howItWorksCTAContent}
          </>
        )
      )}
    </DataCard>
  );
}
